import { CloseOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, Result, Row, Space, Spin } from 'antd';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { StatePieceStatus } from '../../../../../enums';
import { IUpdateInvoiceStatusLookupListFormValues } from '../../../../../interfaces/lookup/invoiceStatus';
import { IUpdateInvoiceStatus } from '../../../../../interfaces/lookup/invoiceStatus/data';
import { ContentHeader } from '../../../../../shared/components/appSkeleton/contentHeader/ContentHeader';
import { FetchResult } from '../../../../../shared/components/fetchResult/FetchResult';
import { ROW_GUTTER } from '../../../../../shared/Constants';
import { isError } from '../../../../../shared/helpers';
import { AppDispatch, RootState } from '../../../../../store';
import { fetchInvoiceStatusById, updateInvoiceStatus } from '../../../../../store/lookupSlice/LookupActions';
import { resetState } from '../../../../../store/lookupSlice/LookupSlice';
import { NotFound } from '../../../../notFound/NotFound';

export const InvoiceStatusLookupListEdit = () => {
    const navigate = useNavigate();
    const params = useParams();
    const dispatch = useDispatch<AppDispatch>();
    const [form] = Form.useForm();
    const lookupSlice = useSelector((state: RootState) => state.lookupSlice);
    const itemId = params.id ? parseInt(params.id) : undefined;

    const onFinish = (values: IUpdateInvoiceStatusLookupListFormValues) => {
        const body: IUpdateInvoiceStatus = {
            id: lookupSlice.detailsPage.invoiceStatus.data?.id,
            ...(values as any),
        };

        dispatch(updateInvoiceStatus(body));
    };

    useEffect(() => {
        if (lookupSlice.detailsPage.invoiceStatus.updateStatus === StatePieceStatus.Success) {
            navigate('/admin/lookup-lists/invoice-status', { replace: true });
        }
    }, [lookupSlice.detailsPage.invoiceStatus.updateStatus]);

    useEffect(() => {
        if (itemId) {
            dispatch(fetchInvoiceStatusById(itemId));
        }

        return () => {
            dispatch(resetState());
        };
    }, [itemId]);

    useEffect(() => {
        if (lookupSlice.detailsPage.invoiceStatus.data) {
            form.setFieldsValue({
                id: lookupSlice.detailsPage.invoiceStatus.data.id,
                code: lookupSlice.detailsPage.invoiceStatus.data.code,
                description: lookupSlice.detailsPage.invoiceStatus.data.description,
            } as IUpdateInvoiceStatusLookupListFormValues);
        }
    }, [lookupSlice.detailsPage.invoiceStatus.data]);

    if (lookupSlice.detailsPage.invoiceStatus.fetchStatus === StatePieceStatus.Error) {
        return <Result status="warning" title="Data wasn't found or something went wrong." />;
    }

    if (!itemId) {
        return <NotFound />;
    }

    return (
        <Form
            form={form}
            name="invoicestatus-lookup-edit"
            onFinish={onFinish}
            layout="vertical"
            onValuesChange={(changedValue: any, values) => {
                form.setFieldsValue(values);
            }}
        >
            <ContentHeader
                title="Manage invoice status lookup list"
                breadcrumbs={[
                    {
                        breadcrumbName: 'Admin',
                        path: '/admin',
                    },
                    {
                        breadcrumbName: 'Manage lookup lists',
                        path: '/admin/lookup-lists',
                    },
                    {
                        breadcrumbName: 'Invoice status',
                        path: '/admin/lookup-lists/invoice-status',
                    },
                    {
                        breadcrumbName: lookupSlice.detailsPage.invoiceStatus.data?.description || '-',
                        path: `/admin/lookup-lists/invoice-status/${params.id}`,
                    },
                    {
                        breadcrumbName: 'Edit',
                        path: `/admin/lookup-lists/invoice-status/${params.id}/edit`,
                    },
                ]}
            >
                {!isError(lookupSlice.detailsPage.invoiceStatus.fetchStatus) && (
                    <Form.Item>
                        <Space>
                            <Button
                                icon={<CloseOutlined />}
                                onClick={() => navigate(-1)}
                                disabled={
                                    lookupSlice.detailsPage.invoiceStatus.updateStatus === StatePieceStatus.IsFetching ||
                                    lookupSlice.detailsPage.invoiceStatus.fetchStatus === StatePieceStatus.IsFetching
                                }
                            >
                                Close
                            </Button>
                            <Button
                                htmlType="submit"
                                type="primary"
                                icon={<SaveOutlined />}
                                loading={
                                    lookupSlice.detailsPage.invoiceStatus.updateStatus === StatePieceStatus.IsFetching ||
                                    lookupSlice.detailsPage.invoiceStatus.fetchStatus === StatePieceStatus.IsFetching
                                }
                            >
                                Save and close
                            </Button>
                        </Space>
                    </Form.Item>
                )}
            </ContentHeader>

            <div className="page-content">
                <Spin spinning={lookupSlice.detailsPage.invoiceStatus.fetchStatus === StatePieceStatus.IsFetching}>
                    {isError(lookupSlice.detailsPage.invoiceStatus.fetchStatus) ? (
                        <FetchResult status={lookupSlice.detailsPage.invoiceStatus.fetchStatus} about={'Invoice status'} />
                    ) : (
                        <>
                            <Row>
                                <Col span={24}>
                                    <Space>
                                        <label className="group-label">Invoice status Information</label>
                                    </Space>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property required">Code</label>
                                    <p>{lookupSlice.detailsPage.invoiceStatus?.data?.code || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">Description</label>
                                    <Form.Item name="description" rules={[{ required: true, message: "'Description' is required" }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>

                            {/* INTERNAL INFORMATION */}
                            <Row className="margin-top">
                                <Col span={24}>
                                    <label className="group-label">Internal Information</label>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property">Created by</label>
                                    <p>{lookupSlice.detailsPage.invoiceStatus?.data?.createdBy?.name || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Created on</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.invoiceStatus.data?.created
                                            ? dayjs(lookupSlice.detailsPage.invoiceStatus.data?.created).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property">Last modified by</label>
                                    <p>{lookupSlice.detailsPage.invoiceStatus.data?.modifiedBy?.name || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Last modified on</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.invoiceStatus.data?.modified
                                            ? dayjs(lookupSlice.detailsPage.invoiceStatus.data?.modified).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>
                        </>
                    )}
                </Spin>
            </div>
        </Form>
    );
};
